import { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';

import { color, Icon, Mode, Select, Tabs } from '@wf/components';
import { GlobalLoader, PageHeader } from '@wf/sw-ui-kit';

import {
  featureFlagsSelector,
  licenseTypeAdditionalFieldsByTabsSelector,
  selectedLicenseTypeNatureSelector,
  selectedLicenseTypeSelector,
} from '../../../License/store/selectors';
import OptionWithTwoDescriptions from '../../../../components/OptionWithTwoDescriptions';
import AttachedDocumentsComparison from '../../components/AttachedDocumentsComparison';
import { setSelectedLicenseTypeId } from '../../../License/store/slice';
import ContentContainer from '../../../../components/ContentContainer';
import { getLicenseTypeById } from '../../../License/store/actions';
import { ArrayFields } from '../../../License/types/fieldEnums';
import ItemsComparison from '../../components/ItemsComparison';
import ComparisonView from '../../components/ComparisonView';
import FeesComparison from '../../components/FeesComparison';
import { Tab } from '../../../License/types/enums';

import useComparisonParams from '../../hooks/useComparisonParams';
import useOperationName from '../../../../hooks/useOperationName';
import useRedirect from '../../../../hooks/useRedirect';
import { LicenseTypeNature } from '../../../../types';
import useTabFields from '../../hooks/useTabFields';

import { documentHistorySelector, versionComparisonSelector } from '../../../../store/audit/selectors';
import { getDateFromTimestamp, getSelectValueFromTimeStamp, getTabChanges } from '../../helpers';
import { resetDocumentHistory, resetVersionComparison } from '../../../../store/audit/slices';
import { getDocumentHistory, getVersionComparison } from '../../../../store/audit/actions';
import namesAndPartiesTabFields from '../../helpers/namesAndPartiesTabFields';

import styles from './VersionComparison.module.scss';

function VersionComparison() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id = '' } = useParams();

  const { leftEpochMillis, rightEpochMillis, changeSearchParams } = useComparisonParams();

  const { loading: licenseTypeLoading } = useSelector(selectedLicenseTypeSelector);
  const { data: documentHistory } = useSelector(documentHistorySelector);
  const { differences, lpcoResponse, isLoading } = useSelector(versionComparisonSelector);
  const additionalFieldsByTab = useSelector(licenseTypeAdditionalFieldsByTabsSelector);
  const licenseTypeNature = useSelector(selectedLicenseTypeNatureSelector);
  const isEPhyto = licenseTypeNature === LicenseTypeNature.EPHYTO;
  const featureFlags = useSelector(featureFlagsSelector);
  const itemTabEnabled = featureFlags?.itemTabEnabled;

  useRedirect();

  const { additionalFields = [], dbOptionAdditionalFields = [], fees } = lpcoResponse;

  const { licenseTypeId } = lpcoResponse;

  const getOperationName = useOperationName();

  useEffect(() => {
    if (licenseTypeId) {
      dispatch(getLicenseTypeById(licenseTypeId));
    }

    return () => {
      dispatch(setSelectedLicenseTypeId(null));
    };
  }, [dispatch, licenseTypeId]);

  useEffect(() => {
    dispatch(getDocumentHistory(id));

    return () => {
      dispatch(resetDocumentHistory());
    };
  }, [dispatch, id]);

  const selectOptions = useMemo(
    () =>
      documentHistory.map(({ modifiedOnEpoch, operation, modifiedBy }) => ({
        label: getDateFromTimestamp(modifiedOnEpoch),
        value: modifiedOnEpoch,
        name: getOperationName(operation),
        name2: modifiedBy,
      })),
    [documentHistory, getOperationName]
  );

  useEffect(() => {
    dispatch(getVersionComparison({ documentId: id, leftEpochMillis, rightEpochMillis }));
  }, [dispatch, id, leftEpochMillis, rightEpochMillis]);

  useEffect(
    () => () => {
      dispatch(resetVersionComparison());
    },
    [dispatch]
  );

  const handleSelectChange = (key: 'leftEpochMillis' | 'rightEpochMillis', value: SelectOption) => {
    changeSearchParams(key, value.value);
  };

  const tabChanges = useMemo(
    () =>
      getTabChanges({
        differences,
        additionalFieldsByTab,
        additionalFields,
        dbOptionAdditionalFields,
        licenseTypeNature,
      }),
    [additionalFields, additionalFieldsByTab, dbOptionAdditionalFields, differences, licenseTypeNature]
  );

  const { headerTabFields, consignmentTabFields } = useTabFields();

  const navigateBack = () => navigate(`../../documentHistory/${id}`, { relative: 'path' });

  const loading = isLoading || licenseTypeLoading;
  const showFeesTab = fees?.length || differences.some((difference) => difference.path.startsWith(ArrayFields.fees, 1));

  return (
    <>
      <PageHeader
        actions={
          <div className={styles.form}>
            <Select
              onChange={(value) => handleSelectChange('leftEpochMillis', value as SelectOption)}
              // @ts-expect-error
              components={{ Option: (props) => <OptionWithTwoDescriptions {...props} /> }}
              value={getSelectValueFromTimeStamp(leftEpochMillis)}
              options={selectOptions}
            />
            <div className={styles.compareIcon}>
              <Icon name="ic_compare_arrows" color={color('typography', 30)} size={24} />
            </div>
            <Select
              onChange={(value) => handleSelectChange('rightEpochMillis', value as SelectOption)}
              // @ts-expect-error
              components={{ Option: (props) => <OptionWithTwoDescriptions {...props} /> }}
              value={getSelectValueFromTimeStamp(rightEpochMillis)}
              options={selectOptions}
            />
          </div>
        }
        title={t('Version Comparison')}
        onNavigateBack={navigateBack}
        mode={Mode.view}
      />

      <ContentContainer>
        <Tabs className={styles.tabContainer}>
          <Tabs.TabList>
            <Tabs.Tab hasChange={tabChanges.header}>{t('Header')}</Tabs.Tab>
            <Tabs.Tab hasChange={tabChanges.namesAndParties}>{t('Names & Parties')}</Tabs.Tab>
            {isEPhyto && <Tabs.Tab hasChange={tabChanges.consignment}>{t('Consignment')}</Tabs.Tab>}
            {itemTabEnabled && <Tabs.Tab hasChange={tabChanges.items}>{t('Items')}</Tabs.Tab>}
            <Tabs.Tab hasChange={tabChanges.attachedDocuments}>{t('Attached Documents')}</Tabs.Tab>
            {showFeesTab && <Tabs.Tab hasChange={tabChanges.fees}>{t('Fees')}</Tabs.Tab>}
          </Tabs.TabList>
          <Tabs.TabPanel className={styles.tabPanel}>
            <ComparisonView fields={headerTabFields} tab={Tab.HEADER} values={lpcoResponse} />
          </Tabs.TabPanel>
          <Tabs.TabPanel className={styles.tabPanel}>
            <ComparisonView fields={namesAndPartiesTabFields} tab={Tab.NAMES_AND_PARTIES} values={lpcoResponse} />
          </Tabs.TabPanel>
          {isEPhyto && (
            <Tabs.TabPanel className={styles.tabPanel}>
              <ComparisonView fields={consignmentTabFields} tab={Tab.CONSIGNMENT} values={lpcoResponse} />
            </Tabs.TabPanel>
          )}
          {itemTabEnabled && (
            <Tabs.TabPanel className={styles.listTabPanel}>
              <ItemsComparison />
            </Tabs.TabPanel>
          )}
          <Tabs.TabPanel className={styles.listTabPanel}>
            <AttachedDocumentsComparison />
          </Tabs.TabPanel>
          {showFeesTab && (
            <Tabs.TabPanel>
              <FeesComparison />
            </Tabs.TabPanel>
          )}
        </Tabs>
        {loading && <GlobalLoader />}
      </ContentContainer>
    </>
  );
}

export default VersionComparison;
